import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import type { LogEntry } from "../API/Models";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

interface UseLogsDataOptions {
	limit: number;
	includeDebug?: boolean;
	tags?: string[];
	/** Filtre de niveau SERVEUR ("all" ou absent = tous). Sans lui, la page ne chargerait que les
	 * N dernieres entrees toutes confondues et un niveau rare (Debug, Error) paraitrait vide. */
	level?: string;
	/** Filtre de type SERVEUR (ex. "content-save"). */
	type?: string;
	/** Recherche plein texte SERVEUR (titre, corps, acteur). */
	search?: string;
}

/**
 * System log feed (GET /api/logs) for the Logs page and the dashboard activity widget. Fetches once
 * per option change; live additions arrive separately over the realtime "activity" stream and are
 * merged by the consumer, so there is no polling loop here. `loading` = initial fetch (skeletons),
 * `isRefreshing` = manual refresh() with the stale list kept on screen. `tags` is joined to a CSV
 * inside useMemo so a caller passing a fresh array literal each render doesn't retrigger the fetch.
 */
export const useLogsData = ({ limit, includeDebug = true, tags, level, type, search }: UseLogsDataOptions) => {
	const [logs, setLogs] = useState<LogEntry[]>([]);
	const [loading, setLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [lastUpdated, setLastUpdated] = useState<string | null>(null);
	const isMountedRef = useRef(true);

	const tagQuery = useMemo(() => {
		if (!tags || tags.length === 0) {
			return undefined;
		}
		return tags.join(",");
	}, [tags]);

	const fetchLogs = useCallback(
		async (manual = false) => {
			if (manual) {
				setIsRefreshing(true);
			} else {
				setLoading(true);
			}
			setError(null);
			try {
				// /api/logs/paged filtre COTE SERVEUR (niveau, type, texte) : la page recoit les N
				// dernieres entrees DU FILTRE, jamais un filtre client sur une tranche brute.
				const response = await axios.get(`${apiBase}/api/logs/paged`, {
					params: {
						page: 1,
						pageSize: limit,
						includeDebug,
						tags: tagQuery,
						level: level && level !== "all" ? level : undefined,
						type: type && type.trim() ? type.trim() : undefined,
						search: search && search.trim() ? search.trim() : undefined
					},
					withCredentials: true
				});
				if (!isMountedRef.current) return;
				setLogs((response.data?.items as LogEntry[]) ?? []);
				setLastUpdated(new Date().toISOString());
			} catch (caughtError) {
				if (!isMountedRef.current) return;
				console.error(caughtError);
				setError("Unable to load logs. Please try again.");
			} finally {
				if (!isMountedRef.current) return;
				if (manual) {
					setIsRefreshing(false);
				} else {
					setLoading(false);
				}
			}
		},
		[includeDebug, limit, tagQuery, level, type, search]
	);

	useEffect(() => {
		isMountedRef.current = true;
		fetchLogs();
		return () => {
			isMountedRef.current = false;
		};
	}, [fetchLogs]);

	return {
		logs,
		loading,
		error,
		lastUpdated,
		isRefreshing,
		refresh: () => fetchLogs(true)
	};
};

export default useLogsData;

